/**
 * Site-wide bundle (assets/search-card-titles.js): a style's search card shows
 * the style's name without a colour. Repo root `Search Card Style Titles.md`.
 *
 * Loaded on every page by layout/theme.liquid, and only while the shop metafield
 * `custom.style_card_titles` is true (the kill switch, ST-6). The Searchanise
 * dropdown sits in the header of every page, and wp-custom.js loads only on
 * product and cart pages.
 *
 * ⚠ Never use a bare `$` here. Every bundle gets scripts/jquery-shim.js injected,
 * which captures the theme's jQuery when this runs; vendor.min.js later swaps
 * `window.jQuery` for another copy. Searchanise fires its events on its OWN
 * jQuery (`window.Searchanise.$`), so that is the one we bind on. The dot in
 * `Searchanise.ResultsUpdated` is a jQuery namespace: a native listener never fires.
 *
 * ⚠ A handler must never throw. Searchanise calls it in the middle of its own
 * draw (the dropdown's `show()` runs after `AutocompleteUpdated`), so one uncaught
 * error would stop the as-you-type dropdown opening on every page.
 */

import { applyToAllSearchaniseRoots, applyToSearchaniseRoot, applyToThemeCards } from "./search-card-titles/dom.js";

function safely(run: () => void): void {
  try {
    run();
  } catch (error) {
    console.warn("search-card-titles:", error);
  }
}

/** Searchanise passes its own jQuery objects, not elements. */
function elementsOf(arg: unknown): Element[] {
  if (arg instanceof Element) return [arg];
  if (typeof arg === "object" && arg !== null && "jquery" in arg) {
    const get = (arg as { get?: unknown }).get;
    if (typeof get === "function") {
      const items: unknown = get.call(arg);
      if (Array.isArray(items)) return items.filter((item): item is Element => item instanceof Element);
    }
  }
  return [];
}

function applyToRoots(arg: unknown): void {
  const roots = elementsOf(arg);
  if (roots.length === 0) {
    applyToAllSearchaniseRoots();
    return;
  }
  for (const root of roots) applyToSearchaniseRoot(root);
}

let searchaniseBound = false;

function bindSearchanise(): void {
  if (searchaniseBound) return;
  const searchaniseJQuery = window.Searchanise?.$;
  if (typeof searchaniseJQuery !== "function") return;
  searchaniseBound = true;

  // Fires after every results draw: a search, a page appended by scrolling, a
  // sort or filter, the grid/list toggle, and every window resize.
  searchaniseJQuery(document).on("Searchanise.ResultsUpdated", (_event: unknown, root: unknown) => {
    safely(() => applyToRoots(root));
  });
  // Fires after every dropdown draw, before the dropdown is shown: [input, dropdown].
  searchaniseJQuery(document).on("Searchanise.AutocompleteUpdated", (_event: unknown, _input: unknown, root: unknown) => {
    safely(() => applyToRoots(root));
  });
  safely(applyToAllSearchaniseRoots);
}

function start(): void {
  if (window.__wpSearchCardTitlesBooted) return;
  window.__wpSearchCardTitlesBooted = true;

  // Searchanise is injected on window `load`, after this deferred bundle. It sets
  // `Loaded`, dispatches the native `Searchanise.Loaded` event, then draws.
  if (window.Searchanise?.Loaded === true) {
    safely(bindSearchanise);
  } else {
    document.addEventListener("Searchanise.Loaded", () => safely(bindSearchanise), { once: true });
  }

  // Collection and native search grids: Liquid shortened them; put a colour back on a collision.
  safely(() => applyToThemeCards(document));
  // The theme's facet filters replace the grid in place, then announce it.
  document.addEventListener("filters-applied", () => safely(() => applyToThemeCards(document)));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
